import UnfoldMore from "@mui/icons-material/UnfoldMore";
import { Breadcrumbs, Button } from "@mui/material";
import { usePopover } from "../../../utils/hooks/use-popover";
import {
  ProgramTreeList,
  ProgramTreePopover,
} from "../../program-tree/components/program-switcher";
import { useProgramFavourites } from "../hooks/use-program-favourites";

import type { ProgramTree } from "../../program-tree/program-tree";
import type { FC } from "react";

/**
 * The tree with a star on each favourite. It loads the favourites each time the switcher opens, so
 * a star changed on the page below shows here too.
 */
const FavouriteProgramTree: FC<{ tree: ProgramTree; onPick: () => void }> = ({ tree, onPick }) => {
  const { favourites } = useProgramFavourites();

  return (
    <ProgramTreeList
      tree={tree}
      favourites={favourites}
      label="Find a program or folder"
      empty="No programs or folders match that name."
      onPick={onPick}
    />
  );
};

/**
 * The breadcrumbs of the landing page. Programs is the current page, and opens the tree of all
 * programs and folders.
 */
export const ProgramsSwitcher: FC<{ tree: ProgramTree }> = ({ tree }) => {
  const [anchorRef, popover] = usePopover<HTMLButtonElement>();

  return (
    <>
      <Breadcrumbs aria-label="Breadcrumb">
        <Button
          ref={anchorRef}
          onClick={popover.handleToggle}
          aria-current="page"
          aria-haspopup="dialog"
          aria-expanded={popover.open}
          endIcon={<UnfoldMore />}
          color="inherit"
          sx={{ fontWeight: 600 }}
          data-analytics-id="programs-switcher-open"
        >
          Programs
        </Button>
      </Breadcrumbs>

      <ProgramTreePopover
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        open={popover.open}
        onClose={popover.handleClose}
        label="All programs and folders"
      >
        <FavouriteProgramTree tree={tree} onPick={popover.handleClose} />
      </ProgramTreePopover>
    </>
  );
};
